import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { GetHelper } from 'src/app/helpers/utils';
import { ApiService } from 'src/app/services/api/api.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { SignupService } from 'src/app/services/signup/signup.service';
import { ToastService } from 'src/app/services/toast/toast.service';

export type PersonAnalysisValues = {
  deposits_and_withdrawals: string;
  future_rescue_need: string;
  id: number;
  investment_assets: string;
  application_purpose: string;
  investment_experience: string;
  monthly_income: string;
  profile: number;
  risk_profile: string;
  total_assets_investments: string;
  twelve_month_investment: string;
  twenty_four_month_application: string;
  score: number;
  disqualification: boolean;
  validity: string;
  expire_register?: string;
  start_date: string;
  not_declared?: boolean;
  is_exempt?: boolean;
};

export type QuizProps = {
  title: string;
  formControl: string;
  suboptions: {
    label: string;
    value: string;
    score?: number;
  }[];
};

@Component({
  selector: 'app-signup-unified-person-analysis',
  templateUrl: './signup-unified-person-analysis.component.html',
  styleUrls: ['./signup-unified-person-analysis.component.scss'],
})
export class SignupUnifiedPersonAnalysisComponent implements OnInit, OnDestroy {
  form = this.formBuilder.group({
    investment_experience: [null, [Validators.required]],
    risk_profile: [null, [Validators.required]],
    future_rescue_need: [null, [Validators.required]],
    application_purpose: [null, [Validators.required]],
    monthly_income: [null, [Validators.required]],
    investment_assets: [null, [Validators.required]],
    total_assets_investments: [null, [Validators.required]],
    // deposits_and_withdrawals: [null, [Validators.required]],
    twelve_month_investment: [null, [Validators.required]],
    twenty_four_month_application: [null, Validators.required],
  });

  data: PersonAnalysisValues;
  event: any;
  sending = false;
  refusing = false;
  loading = true;
  refuseModal = false;
  showConfirmRefuse = false;
  redirectWarning = false;
  not_declared = false;
  showDisqualificationModal = false;
  showDispenseModal = false;
  showQuestionnaire = true;
  funds: any;

  options: QuizProps[] = [
    {
      title:
        'Como você classificaria sua experiência em investimentos com sua formação acadêmica',
      formControl: 'investment_experience',
      suboptions: [
        {
          label:
            'Nula: Não tenho nenhum conhecimento em nenhum tipo de investimento e não tenho formação acadêmica na área financeira',
          value: 'primary',
          score: 1,
        },
        {
          label:
            'Limitada: Tenho pouca experiência em investimentos, apesar de não ter formação acadêmica na área financeira',
          value: 'limited',
          score: 2,
        },
        {
          label:
            'Moderada: Tenho alguma experiência em investimentos e possuo formação acadêmica na área financeira',
          value: 'moderate',
          score: 3,
        },
        {
          label:
            'Extensa: Sou um investidor ativo e experiente, sinto-me confortável em tomar minhas próprias decisões de Investimento',
          value: 'extensive',
          score: 4,
        },
      ],
    },
    {
      title:
        'Qual é a sua sensibilidade em relação aos rendimentos de seus investimentos e seu apetite de risco?',
      formControl: 'risk_profile',
      suboptions: [
        {
          label: 'Não pretendo assumir riscos',
          value: 'primary',
          score: 1,
        },
        {
          label:
            'Aceito uma rentabilidade menor por um investimento que apresente baixo risco',
          value: 'low',
          score: 2,
        },
        {
          label:
            'Aceito um determinado risco por um investimento que possibilite uma rentabilidade maior',
          value: 'average',
          score: 3,
        },
        {
          label:
            'Aceito um risco maior por um investimento que possibilite elevada rentabilidade',
          value: 'high',
          score: 4,
        },
      ],
    },
    {
      title:
        'Considerando a sua necessidade futura, qual seu horizonte de investimentos, ou seja, em quanto tempo você pretende resgatar seus investimentos?',
      formControl: 'future_rescue_need',
      suboptions: [
        {
          label: 'em até 6 meses',
          value: 'six_months',
          score: 1,
        },
        {
          label: 'de 7 a 12 meses (até 1 ano)',
          value: 'one_year',
          score: 2,
        },
        {
          label: 'de 13 a 24 meses (até 2 anos)',
          value: 'two_years',
          score: 3,
        },
        {
          label: 'acima de 25 meses (acima de 2 anos)',
          value: 'over_two_years',
          score: 4,
        },
      ],
    },
    {
      title: 'Qual a finalidade das suas aplicações?',
      formControl: 'application_purpose',

      suboptions: [
        {
          label: 'Consumo',
          value: 'consumption',
          score: 1,
        },
        {
          label: 'Proteção do capital contra a inflação',
          value: 'capital_protection',
          score: 2,
        },
        {
          label: 'Aposentadoria',
          value: 'retirement',
          score: 3,
        },
        {
          label: 'Crescimento do patrimônio',
          value: 'wealth_growth',
          score: 4,
        },
      ],
    },
    {
      title:
        'Assinale abaixo em qual alternativa o valor da receita mensal melhor se encaixa',
      formControl: 'monthly_income',
      suboptions: [
        {
          label: 'até R$ 5.000,00',
          value: 'up_to_five_thousand',
          score: 1,
        },
        {
          label: 'de R$ 5.001,00 a R$ 10.000,00',
          value: 'up_to_ten_thousand',
          score: 2,
        },
        {
          label: 'de R$10.001,00 a R$ 30.000,00',
          value: 'up_to_thirty_thousand',
          score: 3,
        },
        {
          label: 'acima de R$ 30.000,00',
          value: 'over_thirty_thousand',
          score: 4,
        },
      ],
    },
    {
      title: 'Quais Ativos de investimento abaixo compõem seu patrimônio?',
      formControl: 'investment_assets',
      suboptions: [
        {
          label: 'ainda não tenho investimentos',
          value: 'primary',
          score: 1,
        },
        {
          label:
            'Títulos públicos, CDB, poupança, Fundos de investimentos de Renda Fixa e DI, imóveis, móveis (veículos, jóias etc)',
          value: 'low',
          score: 2,
        },
        {
          label:
            'Títulos públicos, CDB, poupança, Fundos de investimentos de Renda Fixa e DI, imóveis, ações, Fundos de Investimentos em Ações, Fundos de Previdência, móveis (veículos, jóias etc)',
          value: 'average',
          score: 3,
        },
        {
          label:
            'Títulos públicos, CDB, poupança, Fundos de Investimentos em Renda Fixa e DI, Imóveis, Ações, Fundos de Investimentos em Ações, Fundos de Previdência, Derivativos, Fundos de Investimentos Multimercado, Fundos de Investimentos em Renda Fixa de Médio e Alto Risco, , móveis ( veículos, joias, etc..).',
          value: 'high',
          score: 4,
        },
      ],
    },
    {
      title:
        'Considerando seus ativos e investimentos financeiros, quais valores compõem o total de seu patrimônio?',
      formControl: 'total_assets_investments',
      suboptions: [
        {
          label: 'Ainda não tenho ativos e investimentos investidos',
          value: 'no_investments',
          score: 1,
        },
        {
          label: 'R$ 1.000,00 a R$ 10.000,00',
          value: 'up_to_ten_thousand',
          score: 2,
        },
        {
          label: 'R$ 10.000,01 a R$ 50.000,00',
          value: 'up_to_fifty_thousand',
          score: 3,
        },
        {
          label: 'acima de R$ 50.000,00',
          value: 'over_fifty_thousand',
          score: 4,
        },
      ],
    },
    // {
    //   title:
    //     'Em relação ao planejamento e necessidade de recursos investidos, qual melhor sentença descreve o planejamento com relação a depósitos e retiradas dos recursos nos próximos anos? *',
    //   formControl: 'deposits_and_withdrawals',
    //   suboptions: [
    //     {
    //       label:
    //         'Retirar parcelas de forma regular como auxílio para despesas da instituição',
    //       value: 'regular_withdrawals',
    //     },
    //     {
    //       label: 'Não adicionar nenhum dinheiro, tampouco fazer retiradas',
    //       value: 'no_deposits_or_withdrawals',
    //     },
    //     {
    //       label:
    //         'Adicionar esporadicamente apenas modestas parcelas de novos investimentos',
    //       value: 'sporadic_deposits',
    //     },
    //     {
    //       label:
    //         'Adicionar novas parcelas de investimentos ao portfólio nos próximos anos',
    //       value: 'new_investment_installments',
    //     },
    //   ],
    // },
    {
      title:
        'Quais investimentos a instituição realizou frequentemente nos últimos 12 meses?',
      formControl: 'twelve_month_investment',
      suboptions: [
        {
          label:
            'Trata-se do primeiro investimento, não realizou nenhuma operação',
          value: 'first_investment',
          score: 1,
        },
        {
          label: 'Investiu apenas em Poupança',
          value: 'savings',
          score: 2,
        },
        {
          label:
            'Investiu em Renda fixa (CDB, fundos multimercado/ renda fixa)',
          value: 'fixed_income',
          score: 3,
        },
        {
          label:
            'Investiu em Renda variável (ações, derivativos ou fundos de ações)',
          value: 'variable_income',
          score: 4,
        },
      ],
    },
    {
      title:
        'Considerando os últimos 24 meses, quantas aplicações/operações foram realizadas com produtos de investimento?',
      formControl: 'twenty_four_month_application',
      suboptions: [
        {
          label: 'Nunca realizou aplicações/operações',
          value: 'never',
          score: 1,
        },
        {
          label: 'Realizou até 5 aplicações/operações',
          value: 'up_to_five',
          score: 2,
        },
        {
          label: 'Realizou entre 6 e 15 aplicações/operações',
          value: 'up_to_fifteen',
          score: 3,
        },
        {
          label: 'Realizou mais de 16 aplicações/operações',
          value: 'over_fifteen',
          score: 4,
        },
      ],
    },
  ];

  constructor(
    private formBuilder: FormBuilder,
    private signupService: SignupService,
    private api: ApiService,
    private toast: ToastService,
    private authService: AuthService,
    private route: ActivatedRoute,
  ) {}

  ngOnDestroy(): void {
    this.event && this.event.unsubscribe();
  }

  ngOnInit(): void {
    // this.event = this.signupService.sendPersonData.subscribe((value) => {
    //   if (value === 'analysis') {
    //     this.sendData();
    //   }
    // });

    this.canProceed();

    // this.form.statusChanges.subscribe((status) => {
    //   this.signupService.setPersonFormStatus('analysis', status === 'VALID');
    // });
  }

  getPoints() {
    let score = 0;
    let profile_label = '';

    this.options.forEach((option) => {
      const value = this.form.get(option.formControl).value;
      const suboption = option.suboptions.find((sub) => sub.value === value);

      if (suboption) {
        score += suboption.score;
      }
    });

    if (score >= 9 && score <= 18) {
      profile_label = 'Conservador';
    } else if (score >= 19 && score <= 28) {
      profile_label = 'Moderado';
    } else if (score > 28) {
      profile_label = 'Agressivo';
    }

    return profile_label;
  }

  async canProceed() {
    // Removida a chamada desnecessária para pf_general/me/
    await Promise.all([this.getData(), this.getFunds()]);
  }

  async getFunds() {
    try {
      const res = await this.api.get({
        route: 'api/fund',
        token: true,
        params: {
          id: 151,
        },
      });

      this.funds = res;
    } catch (error) {
      console.log(error);
    }
  }

  async getData() {
    this.loading = true;
    try {
      // Sempre pegar o UUID da URL
      const routeId = this.route.snapshot.paramMap.get('id');

      if (!routeId) {
        console.error('Register UUID não encontrado na URL');
        this.loading = false;
        return;
      }

      const { data } = await this.api.get({
        route: 'api/registration/investor-profile-pf/',
        params: { register_uuid: routeId },
        token: true,
      });

      let profileData = null;

      // Se data é um array, pegar o item com o maior id
      if (Array.isArray(data) && data.length > 0) {
        profileData = data.reduce((prev, current) => {
          return prev.id > current.id ? prev : current;
        });
      }
      // Se data é um objeto válido, usar diretamente
      else if (
        data &&
        !Array.isArray(data) &&
        typeof data === 'object' &&
        Object.keys(data).length > 0
      ) {
        profileData = data;
      }

      // Se encontrou dados válidos, preencher o formulário
      if (profileData) {
        const hasScore = profileData.score != null;

        this.form.patchValue({
          ...profileData,
          // Quando a API retorna null, significa a opção "primary" (ex.: "Nula")
          // Isso garante que o radio volte marcado ao clicar em "Responder novamente".
        });

        // Sem score, nenhuma opção do questionário deve aparecer selecionada.
        if (!hasScore) {
          this.form.reset();
        }
        // Só preenche com 'primary' (opção 1) quando existe score salvo e
        // a API retornar null nesses campos.
        else if (!profileData.is_exempt) {
          this.form.patchValue({
            ...(profileData.investment_experience == null && {
              investment_experience: 'primary',
            }),
            ...(profileData.risk_profile == null && {
              risk_profile: 'primary',
            }),
            ...(profileData.investment_assets == null && {
              investment_assets: 'primary',
            }),
          });
        }

        this.not_declared = profileData.not_declared || false;

        // Verifica se tem perfil válido (score, profile, is_exempt ou not_declared)
        const hasProfile =
          profileData.score ||
          this.not_declared ||
          profileData.profile ||
          profileData.is_exempt;

        if (this.not_declared || profileData.is_exempt) {
          this.form.reset();
          // Se é "Não declarado" ou "Dispensado por norma", esconde o questionário inicialmente
          this.showQuestionnaire = false;
        } else if (hasProfile) {
          // Se já tem perfil válido, esconde o questionário inicialmente
          // O questionário só aparece quando o usuário clicar em "Responder novamente"
          this.showQuestionnaire = false;
        } else {
          // Se não tem perfil ainda, mostra o questionário
          this.showQuestionnaire = true;
        }

        // Se o perfil já existe (data existe), significa que foi salvo, então libera o botão
        this.signupService.setPersonFormStatus(
          'analysis',
          hasProfile ? true : this.form.valid,
        );

        this.data = profileData;

        // Garante que o status seja atualizado após carregar os dados
        setTimeout(() => {
          if (
            this.data &&
            (this.data.score ||
              this.not_declared ||
              this.data.profile ||
              this.data.is_exempt)
          ) {
            this.signupService.setPersonFormStatus('analysis', true);
          }
        }, 0);
      } else {
        // Se não há dados, limpar o formulário e não preencher valores padrão
        this.form.reset();
        this.not_declared = false;
        this.data = null;
        this.signupService.setPersonFormStatus('analysis', false);
        // Se não há dados, mostra o questionário
        this.showQuestionnaire = true;
      }
    } catch (error) {
      console.log(error);
    } finally {
      this.loading = false;
    }
  }

  checkProfile() {
    const profile = this.getPoints();
    // Verificação de segurança para evitar erro quando funds ainda não foi carregado
    const fundProfile = this.funds?.client_service?.profile_display ?? '';

    if (this.isProfileCompatible(profile, fundProfile)) {
      this.sendData();

      return;
    }

    this.showDisqualificationModal = true;
  }

  isProfileCompatible(profile, fundProfile) {
    if (
      (fundProfile === 'Moderado' && profile === 'Conservador') ||
      (fundProfile === 'Agressivo' && profile === 'Conservador') ||
      (fundProfile === 'Agressivo' && profile === 'Moderado')
    ) {
      return false;
    }

    return true;
  }

  async sendData(disqualification: boolean = false) {
    this.sending = true;

    try {
      const valuesHelper = new GetHelper(this.form.value);

      const payload = {
        investment_experience:
          valuesHelper.get('investment_experience') === 'primary'
            ? null
            : valuesHelper.get('investment_experience'),
        risk_profile:
          valuesHelper.get('risk_profile') === 'primary'
            ? null
            : valuesHelper.get('risk_profile'),
        future_rescue_need: valuesHelper.get('future_rescue_need'),
        application_purpose: valuesHelper.get('application_purpose'),
        monthly_income: valuesHelper.get('monthly_income'),
        investment_assets:
          valuesHelper.get('investment_assets') === 'primary'
            ? null
            : valuesHelper.get('investment_assets'),
        total_assets_investments: valuesHelper.get('total_assets_investments'),
        // deposits_and_withdrawals: valuesHelper.get('deposits_and_withdrawals'),
        twelve_month_investment: valuesHelper.get('twelve_month_investment'),
        twenty_four_month_application: valuesHelper.get(
          'twenty_four_month_application',
        ),
        not_declared: false,
        disqualification,
        is_exempt: false,
      };

      // Sempre pegar o UUID da URL
      const routeId = this.route.snapshot.paramMap.get('id');

      if (!routeId) {
        console.error('Register UUID não encontrado na URL');
        this.toast.show(
          'info',
          'Erro',
          'UUID do registro não encontrado na URL',
        );
        this.sending = false;
        return;
      }

      const res =
        this.data && this.data.id
          ? await this.api.put({
              route: `api/registration/investor-profile-pf/${this.data.id}/`,
              params: { register_uuid: routeId },
              token: true,
              body: payload,
            })
          : await this.api.post({
              route: 'api/registration/investor-profile-pf/',
              params: { register_uuid: routeId },
              token: true,
              body: payload,
            });

      // Atualiza o step usando register-step - sempre pegar da URL
      //   await this.signupService.updateUserStepPF(
      //     'register',
      //     'conditions',
      //     routeId
      //   );

      this.showDisqualificationModal = false;

      await this.getData();

      // Atualiza o status após salvar os dados
      if (
        this.data &&
        (this.data.score ||
          this.not_declared ||
          this.data.profile ||
          this.data.is_exempt)
      ) {
        this.signupService.setPersonFormStatus('analysis', true);
      }

      // Esconde o questionário após salvar com sucesso
      this.showQuestionnaire = false;

      this.toast.show('info', 'Sucesso', 'Dados salvos com sucesso!');
    } catch (error) {
      console.error(error);

      this.toast.show('error', 'Erro', error.error.message);
    }
    this.sending = false;
  }

  getRiskLabel() {
    if (!this.data) return '';

    if (this.data.is_exempt) return '';
    if (this.not_declared) return 'Não declarado';

    let risk = this.data.profile;
    let label = '';

    switch (risk) {
      case 1:
        label = 'Conservador';
        break;
      case 2:
        label = 'Moderado';
        break;
      case 3:
        label = 'Agressivo';
        break;
      default:
        label = 'Conservador';
        break;
    }

    return label;
  }

  getExpireDate() {
    if (!this.data) return null;
    return this.data.expire_register || this.data.validity || null;
  }

  getEnquadramentoLabel() {
    if (!this.data || this.not_declared) return '';
    return this.data.disqualification ? 'Desenquadrado' : 'Enquadrado';
  }

  toggleRefuseModal(setOpen: boolean) {
    this.refuseModal = setOpen;
    this.showConfirmRefuse = false;
  }

  showQuestionnaireForm() {
    // Mostra o questionário quando clicar em "Declarar perfil de investidor"
    this.showQuestionnaire = true;
  }

  async refuseQuiz(isDismissed: boolean = false) {
    this.refusing = true;
    try {
      const formValues = this.form.value;
      const nullableItems = Object.keys(formValues);

      let payload = isDismissed
        ? {
            is_exempt: true,
          }
        : {
            not_declared: true,
          };

      nullableItems.forEach((item) => {
        payload[item] = null;
      });

      // Sempre pegar o UUID da URL
      const routeId = this.route.snapshot.paramMap.get('id');

      if (!routeId) {
        console.error('Register UUID não encontrado na URL');
        this.toast.show(
          'info',
          'Erro',
          'UUID do registro não encontrado na URL',
        );
        this.sending = false;
        return;
      }

      await this.api.post({
        route: 'api/registration/investor-profile-pf/',
        params: { register_uuid: routeId },
        token: true,
        body: payload,
      });

      this.refuseModal = false;
      this.showDispenseModal = false;

      this.form.reset();

      this.getData();
      this.toast.show('info', 'Sucesso', 'Dados salvos com sucesso!');
    } catch (error) {
      console.log(error);
      this.toast.show('error', 'Erro', error.error.message);
    }
    this.refusing = false;
  }

  redirectUser() {
    this.signupService.changePersonStepEvent.emit('general');
  }

  handleAcceptDisqualification = () => {
    this.sendData(true);
  };

  handleRefuseDisqualification = () => {
    this.showDisqualificationModal = false;
  };

  getRefuseConfirmMessage(): string {
    if (this.data && (this.data.score || this.data.profile)) {
      return '<p>Caso prossiga, seu perfil de investidor atual será removido e definido como "Não declarado".</p><p>Deseja continuar?</p>';
    } else {
      return '<p>Caso prossiga, seu perfil de investidor será definido como "Não declarado" sem responder o questionário.</p><p>Deseja continuar?</p>';
    }
  }

  handleConfirmRefuse = () => {
    this.refuseQuiz();
  };

  handleCancelRefuse = () => {
    this.toggleRefuseModal(false);
  };

  handleReturnToQuestions = () => {
    this.toggleRefuseModal(false);
  };

  handleShowConfirmRefuse = () => {
    this.showConfirmRefuse = true;
  };

  applyNormDispense() {
    this.showDispenseModal = true;
  }

  handleConfirmDispense = () => {
    this.refuseQuiz(true);
  };

  handleCancelDispense = () => {
    this.showDispenseModal = false;
  };
}
